import React from 'react';
import { Helmet } from 'react-helmet';

import Nav from '../components/Nav';
import Footer from '../components/Footer';
import appStoreBadge from '../assets/images/app-store-badge.svg';
import googlePlayBadge from '../assets/images/google-play-badge.svg';
import heroBackyard from '../assets/images/lifestyle/hero-backyard-group.jpg';
import dinnerPhoto from '../assets/images/lifestyle/sierra-white-dupe (1).jpeg';
import tripsPhoto from '../assets/images/lifestyle/grace-mccuistion.jpeg';
import timePhoto from '../assets/images/lifestyle/daniel-bughiu-dupe.jpeg';
import katJosh from '../assets/images/kat-josh.jpg';
import mikeRebekah from '../assets/images/mike-rebekah.jpg';
import charlieAli from '../assets/images/Charlie-and-Ali.jpg';
import scottLeanne from '../assets/images/scott-leanne.jpg';
import amandaMike from '../assets/images/Amanda&Mike.jpg';
import appScreenshot1 from '../assets/images/app-screenshot-1.png';
import appScreenshot2 from '../assets/images/app-screenshot-2.png';
import { useStoreLinks } from '../lib/useStoreLinks';
import { COMMUNITY_STATS } from '../data/stats';

type Benefit = {
  image: string;
  title: string;
  copy: string;
  objectPosition?: string;
};

const benefits: Benefit[] = [
  {
    image: dinnerPhoto,
    title: 'Dinners with friends',
    copy: 'Friend groups that form around dinners, not playgrounds.',
  },
  {
    image: tripsPhoto,
    title: 'Trips worth taking',
    copy: 'Spontaneous weekends with couples who can actually say yes.',
    objectPosition: 'center 85%',
  },
  {
    image: timePhoto,
    title: 'Game nights at home',
    copy: "Hangouts that don't need an occasion.",
  },
];

type Quote = {
  avatar: string;
  name: string;
  quote: string;
};

const quotes: Quote[] = [
  {
    avatar: mikeRebekah,
    name: 'Mike & Rebekah',
    quote:
      'After two years of struggling to meet like-minded couples since moving to New Jersey, we joined DINK Social and within a month we\u2019ve already made four new friends. We\u2019ve got NYE plans, game nights, and a real sense of community now. Honestly, this platform has been a lifesaver for us.',
  },
  {
    avatar: katJosh,
    name: 'Kat & Josh',
    quote:
      'Great app, even greater community of couples! Everyone I\u2019ve met through the DINK Social community has been warm and inviting. If you\u2019re a DINK couple, this is def the place to meet other DINK couples and create meaningful relationships!',
  },
  {
    avatar: scottLeanne,
    name: 'Scott & Leanne',
    quote:
      'DINK Social is the community we\u2019ve been searching for. Like-minded adults unencumbered by finding babysitters and ready for adventures, experiences and just enjoying life. It\u2019s a place where you don\u2019t have to explain "why" and you don\u2019t have to apologize for making the right choices for yourself.',
  },
  {
    avatar: amandaMike,
    name: 'Amanda & Mike',
    quote:
      'We kept hearing that more and more couples were choosing to be DINKs, but we never seemed to meet any. We have a great group of friends with kids, but we were missing connections with other couples who shared a similar lifestyle. After discovering DINK Social, we started attending, and hosting, events in our area and have already made some great friendships within the community.',
  },
];

type SupportPoint = {
  title: string;
  copy: string;
};

const supportPoints: SupportPoint[] = [
  {
    title: 'Real friendships',
    copy: 'Couples who actually get your life',
  },
  {
    title: 'Honest conversations',
    copy: 'Money, family, and planning your future together',
  },
  {
    title: 'A community that gets it',
    copy: 'No justifying, no explaining, no pretending',
  },
];

const HomePage: React.FC = () => {
  const { appStoreUrl, playStoreUrl, onAppStoreClick, onPlayStoreClick } = useStoreLinks();
  return (
    <div className="font-display text-ink bg-white">
      <Helmet>
        <title>DINK Social: Social App and Community for Childfree Couples</title>
        <meta
          name="description"
          content="The social app built exclusively for childfree couples. Connect, discover events, and build real friendships with couples like you."
        />
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://dinksocial.com/" />
        <meta property="og:title" content="DINK Social: Social App and Community for Childfree Couples" />
        <meta property="og:description" content="The social app built exclusively for childfree couples. Connect, discover events, and build real friendships with couples like you." />
        <meta property="og:image" content="https://dinksocial.com/og-image.jpg" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://dinksocial.com/" />
        <meta name="twitter:title" content="DINK Social: Social App and Community for Childfree Couples" />
        <meta name="twitter:description" content="The social app built exclusively for childfree couples. Connect, discover events, and build real friendships with couples like you." />
        <meta name="twitter:image" content="https://dinksocial.com/og-image.jpg" />

        {/* Canonical URL */}
        <link rel="canonical" href="https://dinksocial.com/" />

        {/* Structured Data - Organization + WebSite */}
        <script type="application/ld+json">{`
          {
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "Organization",
                "@id": "https://dinksocial.com/#organization",
                "name": "DINK Social",
                "url": "https://dinksocial.com",
                "logo": {
                  "@type": "ImageObject",
                  "url": "https://dinksocial.com/DINKSocial_logo_plum.svg"
                },
                "description": "The first social app built specifically for childfree couples to connect, discover events, and build meaningful friendships.",
                "foundingDate": "2025",
                "sameAs": [
                  "https://www.instagram.com/dinksocial",
                  "https://www.tiktok.com/@dinksocial"
                ],
                "contactPoint": {
                  "@type": "ContactPoint",
                  "email": "hello@dinksocial.com",
                  "contactType": "Customer Support"
                }
              },
              {
                "@type": "WebSite",
                "@id": "https://dinksocial.com/#website",
                "name": "DINK Social",
                "url": "https://dinksocial.com",
                "description": "The first social app built exclusively for childfree couples. Connect with like-minded DINKs, discover events, and build meaningful friendships.",
                "publisher": { "@id": "https://dinksocial.com/#organization" }
              }
            ]
          }
        `}</script>
      </Helmet>

      <Nav transparentOverHero ctaHref="#get-the-app" />

      {/* ─── HERO — full-bleed lifestyle image ──────────── */}
      <section className="relative h-screen min-h-[640px] w-full overflow-hidden">
        <img
          src={heroBackyard}
          alt=""
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover"
          style={{ filter: 'brightness(1.12) saturate(1.08) contrast(0.98)' }}
        />
        {/* Light warm bottom-only scrim — just enough to anchor the headline, no moodiness */}
        <div className="absolute inset-x-0 bottom-0 h-[55%] bg-gradient-to-t from-black/45 via-black/15 to-transparent" />

        <div className="relative z-10 h-full flex items-end pb-32 md:pb-12">
          <div className="max-w-7xl mx-auto px-6 md:px-10 w-full">
            <div className="max-w-3xl">
              <h1
                className="font-display font-medium text-white leading-[1.0] tracking-[-0.02em] text-[2.75rem] sm:text-6xl md:text-7xl lg:text-[5.5rem]"
                style={{ textShadow: '0 2px 24px rgba(0,0,0,0.35)' }}
              >
                The social app for childfree couples
              </h1>
            </div>
          </div>
        </div>
      </section>

      {/* ─── SECTION 2 — VALUE + PROOF (Timeleft-style) ── */}
      <section className="relative py-10 md:py-20 px-6 md:px-10 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="md:grid md:grid-cols-2 md:gap-x-12 md:items-center">
            {/* Headline — mobile: first; desktop: left col, row 1 */}
            <div className="md:col-start-1 md:row-start-1">
              <h2
                className="font-display font-medium text-ink text-[42px] md:text-[56px] lg:text-[64px] text-left"
                style={{ lineHeight: 1.02, letterSpacing: '-0.02em' }}
              >
                {'Connect with couples '}
                <span className="font-playfair italic font-medium text-primary">like you</span>
              </h2>
              <p
                className="font-playfair font-medium text-ink/85 text-[20px] md:text-[24px] lg:text-[26px] mt-5 md:mt-6"
                style={{ lineHeight: 1.3, letterSpacing: '-0.015em' }}
              >
                No justifying, no explaining, no pretending.
              </p>
            </div>

            {/* Tilted phone mockups — mobile: second; desktop: right col, spans all rows */}
            <div className="flex items-center justify-center mt-10 md:mt-0 md:col-start-2 md:row-start-1 md:row-span-3">
              <img
                src={appScreenshot1}
                alt="DINK Social — events feed"
                loading="lazy"
                decoding="async"
                className="w-[42%] max-w-[240px] md:max-w-[260px] rotate-[-4deg] translate-y-4 drop-shadow-[0_20px_40px_rgba(45,31,61,0.18)] rounded-[2rem] -mr-6 md:-mr-8"
              />
              <img
                src={appScreenshot2}
                alt="DINK Social — community"
                loading="lazy"
                decoding="async"
                className="w-[42%] max-w-[240px] md:max-w-[260px] rotate-[4deg] drop-shadow-[0_20px_40px_rgba(45,31,61,0.18)] rounded-[2rem] relative z-10"
              />
            </div>

            {/* Social proof row — mobile: third; desktop: left col, row 2 */}
            <div className="grid grid-cols-2 gap-6 md:flex md:justify-start md:gap-12 mt-8 md:col-start-1 md:row-start-2">
              {/* Star rating cluster */}
              <div className="flex flex-col items-center text-center">
                <div
                  className="h-10 relative flex items-center text-lg"
                  role="img"
                  aria-label="4.8 out of 5 stars"
                >
                  <div className="flex items-center gap-[3px] text-ink/30" aria-hidden>
                    <span>★</span><span>★</span><span>★</span><span>★</span><span>★</span>
                  </div>
                  <div
                    className="absolute inset-0 flex items-center gap-[3px] text-gold"
                    style={{ clipPath: 'inset(0 4% 0 0)' }}
                    aria-hidden
                  >
                    <span>★</span><span>★</span><span>★</span><span>★</span><span>★</span>
                  </div>
                </div>
                <div className="font-medium text-ink text-2xl leading-none mt-2">
                  4.8
                </div>
                <div className="text-[10px] uppercase tracking-[0.22em] font-medium text-ink/60 mt-2">
                  App Store rating
                </div>
              </div>

              {/* Avatar cluster */}
              <div className="flex flex-col items-center text-center">
                <div className="h-10 flex -space-x-3">
                  {[katJosh, mikeRebekah, charlieAli, scottLeanne].map((src, i) => (
                    <img
                      key={i}
                      src={src}
                      alt=""
                      loading="lazy"
                      decoding="async"
                      className="w-10 h-10 rounded-full object-cover border-2 border-white bg-ink/5"
                    />
                  ))}
                </div>
                <div className="font-medium text-ink text-2xl leading-none mt-2">
                  {COMMUNITY_STATS.cities}
                </div>
                <div className="text-[10px] uppercase tracking-[0.22em] font-medium text-ink/60 mt-2">
                  Cities
                </div>
              </div>
            </div>

            {/* CTA — mobile: fourth; desktop: left col, row 3 */}
            <div className="flex justify-center md:justify-start mt-10 md:mt-8 md:col-start-1 md:row-start-3">
              <a
                href="#get-the-app"
                className="inline-block text-base font-semibold px-10 py-4 rounded-full bg-primary text-white hover:bg-primary-dark transition-colors"
              >
                Get the app
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ─── SECTION 3 — BENEFITS (editorial vertical zigzag) ───── */}
      <section className="relative py-10 md:py-20 px-6 md:px-10 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="space-y-14 md:space-y-20">
            {benefits.map((b, i) => {
              const imgRight = i % 2 === 0;
              return (
                <div
                  key={b.title}
                  className="md:grid md:grid-cols-12 md:gap-10 md:items-center"
                >
                  {/* Copy — DOM-first so it sits above image on mobile */}
                  <div
                    className={
                      imgRight
                        ? 'md:col-start-1 md:col-span-5 md:row-start-1'
                        : 'md:col-start-8 md:col-span-5 md:row-start-1'
                    }
                  >
                    <h3
                      className="font-display font-medium text-ink text-[34px] md:text-[40px] mb-4"
                      style={{ lineHeight: 1.1, letterSpacing: '-0.02em' }}
                    >
                      {b.title}
                    </h3>
                    <p
                      className="text-[17px] md:text-[19px] text-ink/70"
                      style={{ lineHeight: 1.55 }}
                    >
                      {b.copy}
                    </p>
                  </div>

                  {/* Image — DOM-second so it sits below copy on mobile */}
                  <div
                    className={`overflow-hidden rounded-3xl bg-ink/5 aspect-[3/2] mt-6 md:mt-0 ${
                      imgRight
                        ? 'md:col-start-7 md:col-span-6 md:row-start-1'
                        : 'md:col-start-1 md:col-span-6 md:row-start-1'
                    }`}
                  >
                    <img
                      src={b.image}
                      alt={b.title}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover"
                      style={b.objectPosition ? { objectPosition: b.objectPosition } : undefined}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ─── SECTION 3.5 — MORE THAN EVENTS ──────────────── */}
      <section className="relative py-10 md:py-20 px-6 md:px-10 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="max-w-3xl mx-auto mb-12 md:mb-16 text-center">
            <h2
              className="font-display font-medium text-ink text-[42px] md:text-[56px] lg:text-[64px] mb-6"
              style={{ lineHeight: 1.02, letterSpacing: '-0.02em' }}
            >
              More than a night out
            </h2>
            <p
              className="text-[17px] md:text-[19px] text-ink/70"
              style={{ lineHeight: 1.55 }}
            >
              Life without kids comes with its own set of questions, like money as a two-income household, aging parents, retiring early, and getting a will sorted. They're the conversations childfree couples rarely have anywhere good. DINK Social is where you two find people who get it, and come away feeling ready for what's next.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {supportPoints.map((s) => (
              <div
                key={s.title}
                className="border border-ink/10 rounded-3xl p-7 md:p-8 bg-white"
              >
                <h3
                  className="font-display font-medium text-ink text-[20px] md:text-[22px] mb-2"
                  style={{ lineHeight: 1.15, letterSpacing: '-0.015em' }}
                >
                  {s.title}
                </h3>
                <p
                  className="text-[15px] md:text-[16px] text-ink/70"
                  style={{ lineHeight: 1.55 }}
                >
                  {s.copy}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── SECTION 3 — QUOTE CARDS ─────────────────────── */}
      <section className="relative py-10 md:py-20 px-6 md:px-10 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="max-w-3xl mx-auto mb-16 md:mb-20 text-center">
            <h2
              className="font-display font-medium text-ink text-[42px] md:text-[56px] lg:text-[64px]"
              style={{ lineHeight: 1.02, letterSpacing: '-0.02em' }}
            >
              Living fully, connecting freely
            </h2>
          </div>

          <div className="flex md:grid md:grid-cols-2 gap-6 md:gap-8 overflow-x-auto md:overflow-visible snap-x snap-mandatory -mx-6 md:mx-0 px-6 md:px-0 pb-4 md:pb-0">
            {quotes.map((q) => (
              <figure
                key={q.name}
                className="flex-shrink-0 w-[85%] md:w-auto snap-start border border-ink/10 rounded-3xl p-7 md:p-8 bg-white flex flex-col"
              >
                <blockquote
                  className="font-playfair italic font-medium text-ink text-[20px] md:text-[22px] flex-1"
                  style={{ lineHeight: 1.3, letterSpacing: '-0.01em' }}
                >
                  {`“${q.quote}”`}
                </blockquote>
                <figcaption className="mt-6 flex items-center gap-3">
                  <img
                    src={q.avatar}
                    alt={q.name}
                    loading="lazy"
                    decoding="async"
                    className="w-10 h-10 rounded-full object-cover bg-ink/5"
                  />
                  <span className="text-[11px] uppercase tracking-[0.22em] font-medium text-ink/70">
                    {q.name}
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* ─── SECTION 4 — DOWNLOAD MOMENT ─────────────────── */}
      <section
        id="get-the-app"
        className="relative py-10 md:py-14 px-6 md:px-10 bg-primary"
      >
        <div className="max-w-4xl mx-auto text-center">
          <h2
            className="font-display font-medium text-white text-[38px] md:text-[52px] lg:text-[60px] mb-8 md:mb-10"
            style={{ lineHeight: 1.02, letterSpacing: '-0.02em' }}
          >
            Find your people
          </h2>

          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <a
              href={appStoreUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onAppStoreClick}
              className="hover:opacity-80 transition-opacity"
              aria-label="Download on the App Store"
            >
              <img src={appStoreBadge} alt="Download on the App Store" className="h-12 w-auto" />
            </a>
            <a
              href={playStoreUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={onPlayStoreClick}
              className="hover:opacity-80 transition-opacity"
              aria-label="Get it on Google Play"
            >
              <img src={googlePlayBadge} alt="Get it on Google Play" className="h-12 w-auto" />
            </a>
          </div>
        </div>
      </section>

      <Footer bridgeTopSeam />
    </div>
  );
};

export default HomePage;
